import {FirebaseApp, getApp, initializeApp} from 'firebase/app';
import {Auth as FirebaseAuth, getAuth} from 'firebase/auth';
import {Database as FirebaseDatabase, forceWebSockets, getDatabase} from 'firebase/database';
import {FirebaseStorage, getStorage} from 'firebase/storage';
import React from 'react';

// Disable Firebase RTDB's long-polling fallback so we don't have to grant
// `*.firebaseio.com` in script-src / frame-src — that wildcard is
// exploitable because anyone can register a Firebase project under it. The
// fallback is only reached when WebSockets are blocked (rare on modern
// networks); affected users will lose realtime updates but the rest of the
// app still works.
forceWebSockets();

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBZnrCwNoDgg5DFzEP6Smq9cscYxT-9hSo',
  authDomain: 'monitron-dev.firebaseapp.com',
  databaseURL: 'https://monitron-dev.firebaseio.com',
  projectId: 'monitron-dev',
  storageBucket: 'monitron-dev.appspot.com',
  messagingSenderId: '579167012312',
};

interface FirebaseObjects {
  app: FirebaseApp;
  auth: FirebaseAuth;
  database: FirebaseDatabase;
  storage: FirebaseStorage;
}

export const FirebaseContext = React.createContext<FirebaseObjects | undefined>(undefined);

// Allows access to the Firebase app and some of its sub-objects.
//
// Will initialize the app if it hasn’t been, but does not try to delete it on
// unmount.
const FirebaseProvider: React.FunctionComponent<React.PropsWithChildren<{config?: object}>> = ({
  config = FIREBASE_CONFIG,
  children,
}) => {
  let firebaseApp: FirebaseApp;

  try {
    firebaseApp = getApp();
  } catch {
    firebaseApp = initializeApp(config);
  }

  // We definitely need these objects to stay consistent while the app is
  // being used, since they’re dependencies for any useEffect hooks that
  // interact with Firebase.
  const firebaseObjects = React.useMemo(
    () => ({
      app: firebaseApp,
      auth: getAuth(firebaseApp),
      database: getDatabase(firebaseApp),
      storage: getStorage(firebaseApp),
    }),
    [firebaseApp]
  );

  return <FirebaseContext.Provider value={firebaseObjects}>{children}</FirebaseContext.Provider>;
};

const useFirebase = () => {
  const firebaseObjects = React.useContext(FirebaseContext);

  if (!firebaseObjects) {
    throw new Error('This hook must be called beneath FirebaseProvider');
  }

  return firebaseObjects;
};

export const useFirebaseAuth = () => {
  const {auth} = useFirebase();
  return auth;
};

export const useFirebaseDatabase = () => {
  const {database} = useFirebase();
  return database;
};

export const useFirebaseStorage = () => {
  const {storage} = useFirebase();
  return storage;
};

export default FirebaseProvider;
